import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { z } from 'zod';

import {
    createJsaProfileDetailTilePayload,
    createWorkTaskTilePayload,
    isWorkTaskTilePayload,
} from '../job-architecture-v2';
import { createPositionTilePayload, selectPositionIdFromTile } from '../position-management';
import {
    createUrlTileCodec,
    createUrlTileDefinition,
    type UrlTileModule,
    type UrlTileRenderContext,
    useUrlTiles,
} from '../url-tiles';
import { PaletteProvider, usePaletteFromUrl } from './components/PaletteProvider';
import { ItemTile } from './item-tile/ItemTile';
import { StrategyGroupTile } from './strategy/group-tile/StrategyGroupTile';
import { parseStrategyViewState, toQueryFiltersWithoutPick } from './strategy/model/view-state';

export const AI_TRANSFORMATION_ITEM_TILE_TYPE = 'v1.ai-transformation.item' as const;
export const AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY = 'ai-transformation.item';
export const AI_TRANSFORMATION_GROUP_TILE_TYPE = 'v1.ai-transformation.group' as const;
export const AI_TRANSFORMATION_GROUP_TILE_SLOT_KEY = 'ai-transformation.group';
const ITEM_TILE_WIDTH = '520px';
const GROUP_TILE_WIDTH = '440px';

const idSchema = z
    .string()
    .trim()
    .min(1)
    .refine((value) => !value.includes('.'));

export const aiTransformationItemTileSchema = z.object({
    architectureId: idSchema,
    itemId: idSchema,
    type: z.literal(AI_TRANSFORMATION_ITEM_TILE_TYPE),
});

export type AiTransformationItemTilePayload = z.infer<typeof aiTransformationItemTileSchema>;

export const createAiTransformationItemTilePayload = (
    architectureId: string,
    itemId: string,
): AiTransformationItemTilePayload => ({
    architectureId,
    itemId,
    type: AI_TRANSFORMATION_ITEM_TILE_TYPE,
});

type ItemTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AiTransformationItemTilePayload;
};

const ItemTileWithPalette: FunctionComponent<ItemTileDefinitionProps> = ({ context, payload }) => {
    const palette = usePaletteFromUrl();

    return (
        <PaletteProvider palette={palette}>
            <ItemTile
                architectureId={payload.architectureId}
                itemId={payload.itemId}
                onClose={() => context.closeTile(AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY)}
                onOpenJsaItem={({ itemId, layer }) =>
                    context.openTile(
                        createJsaProfileDetailTilePayload({
                            architectureId: payload.architectureId,
                            itemType: layer === 'level' ? 'job-level' : 'job-role',
                            mode: 'view',
                            profileId: itemId,
                        }),
                    )
                }
                onOpenJsaTask={(taskId) => context.openTile(createWorkTaskTilePayload({ taskId }))}
            />
        </PaletteProvider>
    );
};

export const aiTransformationItemUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<AiTransformationItemTilePayload>({
        options: [],
        segments: ['architectureId', 'itemId'],
        type: AI_TRANSFORMATION_ITEM_TILE_TYPE,
    }),
    maxWidth: ITEM_TILE_WIDTH,
    minWidth: ITEM_TILE_WIDTH,
    render: (props: ItemTileDefinitionProps) => <ItemTileWithPalette {...props} />,
    schema: aiTransformationItemTileSchema,
    slotKey: () => AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY,
    tileKey: ({ architectureId, itemId }) => `${AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY}.${architectureId}.${itemId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: ITEM_TILE_WIDTH,
});

export const aiTransformationGroupTileSchema = z.object({
    architectureId: idSchema,
    groupId: idSchema,
    grouping: z.enum(['LEVEL', 'ROLE']),
    type: z.literal(AI_TRANSFORMATION_GROUP_TILE_TYPE),
});

/** A role or level of the strategy matrix; the tile lists its positions under the page's filters, without its pick. */
export type AiTransformationGroupTilePayload = z.infer<typeof aiTransformationGroupTileSchema>;

export const createAiTransformationGroupTilePayload = ({
    architectureId,
    groupId,
    grouping,
}: Omit<AiTransformationGroupTilePayload, 'type'>): AiTransformationGroupTilePayload => ({
    architectureId,
    groupId,
    grouping,
    type: AI_TRANSFORMATION_GROUP_TILE_TYPE,
});

type GroupTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AiTransformationGroupTilePayload;
};

/** Reads the strategy page's filters from the URL it shares with the page and applies them without the page's pick. */
const GroupUrlTile: FunctionComponent<GroupTileDefinitionProps> = ({ context, payload }) => {
    const { tiles } = useUrlTiles();
    const { search } = context.location;
    const filters = useMemo(
        () => toQueryFiltersWithoutPick(parseStrategyViewState(new URLSearchParams(search))),
        [search],
    );

    return (
        <StrategyGroupTile
            architectureId={payload.architectureId}
            filters={filters}
            groupId={payload.groupId}
            grouping={payload.grouping}
            openPositionId={tiles
                .map(({ payload: tile }) => selectPositionIdFromTile(tile))
                .find((id) => id !== undefined)}
            openTaskId={tiles
                .map(({ payload: tile }) => (isWorkTaskTilePayload(tile) ? tile.taskId : undefined))
                .find((id) => id !== undefined)}
            onClose={() => context.closeTile(AI_TRANSFORMATION_GROUP_TILE_SLOT_KEY)}
            onOpenPosition={(positionId) => context.openTile(createPositionTilePayload({ positionId }))}
            onOpenTask={(taskId, version) => context.openTile(createWorkTaskTilePayload({ taskId, version }))}
        />
    );
};

export const aiTransformationGroupUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<AiTransformationGroupTilePayload>({
        options: [],
        segments: ['architectureId', 'grouping', 'groupId'],
        type: AI_TRANSFORMATION_GROUP_TILE_TYPE,
    }),
    maxWidth: GROUP_TILE_WIDTH,
    minWidth: GROUP_TILE_WIDTH,
    render: (props: GroupTileDefinitionProps) => <GroupUrlTile {...props} />,
    schema: aiTransformationGroupTileSchema,
    slotKey: () => AI_TRANSFORMATION_GROUP_TILE_SLOT_KEY,
    tileKey: ({ architectureId, groupId, grouping }) =>
        `${AI_TRANSFORMATION_GROUP_TILE_SLOT_KEY}.${architectureId}.${grouping}.${groupId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: GROUP_TILE_WIDTH,
});

export const aiTransformationUrlTileModule: UrlTileModule = {
    definitions: [aiTransformationItemUrlTileDefinition, aiTransformationGroupUrlTileDefinition],
};
